import React from 'react';
import { Check, Star } from 'lucide-react';

const Packages = () => {
  const packages = [
    {
      id: 'new-client',
      name: 'New Client Special',
      price: 380,
      originalPrice: 520,
      popular: true,
      image: '/food.jpg',
      useEmoji: true,
      emoji: '🎁',
      description: 'Get started with 4 personalized training sessions at a special rate. Perfect for new clients looking to begin their fitness journey.',
      features: [
        '4-pack training sessions for new clients',
        'In-person training sessions',
        'Personalized training program',
        'Form and technique assessment',
        'Nutrition guidance included',
        'Flexible scheduling'
      ],
      badge: 'Limited Time Offer',
      link: 'http://Htphysique.myshopify.com/products/gift-phytraining-4-pack'
    },
    {
      id: 'small-group',
      name: 'Small Group Training',
      price: 135,
      originalPrice: 180,
      image: '/hero/IMG_3049.jpg',
      description: 'Join our small group training sessions for a more affordable yet effective workout experience. Perfect for those who enjoy group motivation.',
      features: [
        'Small group setting',
        'Expert guidance',
        'Motivating atmosphere',
        'Regular schedule',
        'New client discount'
      ],
      link: 'http://Htphysique.myshopify.com/products/group-training-jan-30-to-feb-20'
    },
    {
      id: 'phyt-food',
      name: 'PHYT FOOD Program',
      price: 200,
      image: '/food.jpg',
      description: "Lose 1 inch in your waist line with our four week nutrition coaching program. Learn, simple, sustainable, eating habits for life."
      ,
      features: [
        'Personalized meal plans',
        'Nutrition education',
        'Recipe database',
        'Shopping guides',
        'Ongoing support'
      ],
      link: 'http://Htphysique.myshopify.com/products/phyt-food-2-0'
    },
    {
      id: 'travel-kit',
      name: 'PHYT Travel Kit',
      price: 9.99,
      image: '/kit.jpg',
      description: 'Stay mobile and flexible with our 5-minute mobility program. Perfect for busy professionals and travelers.',
      features: [
        'Quick mobility routines',
        'Travel-friendly exercises',
        'Digital access',
        'No equipment needed',
        'Instant access'
      ],
      link: 'https://my.playbookapp.io/coach-ht/programs/phyt-travel-kit-mobility-in-5-min/26819'
    }
  ];

  return (
    <section className="min-h-screen flex flex-col bg-white overflow-x-hidden" id="packages">
      <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col flex-1 w-full overflow-x-hidden">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-2 text-gray-900">
            Programs & <span className="text-primary-500">Services</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
            Choose from our proven programs designed to get you results faster than traditional methods
          </p>
        </div>

        {/* Horizontal Scrollable Container */}
        <div className="relative flex-1 flex items-center w-full overflow-x-hidden">
          <div 
            className="overflow-x-auto scrollbar-hide pb-6 w-full" 
            style={{ 
              WebkitOverflowScrolling: 'touch',
              scrollBehavior: 'smooth',
              overflowX: 'auto',
              overflowY: 'hidden',
              paddingLeft: '1rem',
              paddingRight: '1rem'
            }}
          >
            <div className="flex gap-6 lg:gap-8" style={{ width: 'max-content' }}>
              {packages.map((pkg) => (
                <div 
                  key={pkg.id} 
                  className={`relative flex-shrink-0 w-80 lg:w-96 h-[calc(100vh-12rem)] rounded-3xl overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl bg-white ${
                    pkg.popular 
                      ? 'ring-2 ring-primary-500 ring-opacity-50 shadow-2xl' 
                      : 'shadow-xl border border-gray-100'
                  }`}
                >
                  <div className="flex flex-col h-full">
                    {/* Image or Emoji - seamlessly integrated with card */}
                    <div className="relative w-full h-64 lg:h-80 flex-shrink-0 overflow-hidden rounded-t-3xl bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
                      {pkg.useEmoji ? (
                        <div className="text-8xl lg:text-9xl">{pkg.emoji}</div>
                      ) : (
                        <>
                          <img 
                            src={pkg.image} 
                            alt={pkg.name}
                            className="w-full h-full object-cover object-center"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent"></div>
                        </>
                      )}
                      {pkg.badge && (
                        <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-primary-500 to-primary-600 text-center px-4 py-1.5 text-xs font-semibold text-white rounded-full shadow-lg">
                          {pkg.badge}
                        </div>
                      )}
                    </div>
                    
                    <div className="p-5 lg:p-6 flex flex-col flex-1 min-h-0">
                      <div className="mb-3">
                        <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2 tracking-tight">{pkg.name}</h3>
                        <div className="flex items-baseline justify-start gap-2">
                          {pkg.originalPrice && (
                            <span className="text-gray-400 line-through text-base">${pkg.originalPrice}</span>
                          )}
                          <span className="text-2xl lg:text-3xl font-bold text-primary-500 tracking-tight">${pkg.price}</span>
                        </div>
                      </div>

                      <p className="text-gray-600 mb-4 text-xs lg:text-sm leading-relaxed">
                        {pkg.description}
                      </p>
                      
                      <div className="flex-1 mb-4 overflow-y-auto min-h-0">
                        <h4 className="font-semibold mb-2 text-gray-900 text-xs uppercase tracking-wide">What's Included:</h4>
                        <ul className="space-y-2">
                          {pkg.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="flex-shrink-0 mt-0.5 text-primary-500">
                                <Check size={16} strokeWidth={2.5} />
                              </span>
                              <span className="text-gray-700 text-xs lg:text-sm leading-relaxed">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <button 
                        onClick={() => {
                          const url = pkg.link + '?t=' + Date.now();
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }}
                        className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white py-3 px-6 rounded-2xl font-semibold hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-center text-sm flex-shrink-0"
                      >
                        Start Today
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <style jsx>{`
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          @media (max-width: 768px) {
            section#packages {
              overflow-x: hidden;
            }
            section#packages > div {
              overflow-x: hidden;
            }
          }
        `}</style>
      </div>
    </section>
  );
};

export default Packages; 